import { HTTP_STATUS } from '@/app/config/constants';
import type { ApiError, ErrorType } from './types';

/**
 * Fallback copy, used when the server gives us nothing usable.
 *
 * The network, server and rate-limit strings are verbatim from the error
 * matrix in mdfiles/loginapi.md section 8.
 */
const MESSAGES: Record<ErrorType, string> = {
  NETWORK_ERROR: 'Network connection error',
  TIMEOUT_ERROR: 'The request timed out. Please try again.',
  VALIDATION_ERROR: 'Please check your input.',
  AUTHENTICATION_ERROR: 'Session expired. Please login again.',
  AUTHORIZATION_ERROR: "You don't have permission to do that.",
  NOT_FOUND_ERROR: 'We could not find what you were looking for.',
  RATE_LIMIT_ERROR: 'Too many attempts, please wait',
  SERVER_ERROR: 'Server error, please try again',
  UNKNOWN_ERROR: 'Something went wrong. Please try again.',
};

/**
 * Codes whose fallback copy always beats the raw message. Axios reports
 * "Network Error" / "timeout of 30000ms exceeded", which are useless to a user.
 */
const CODE_WINS: ReadonlySet<ErrorType> = new Set<ErrorType>([
  'NETWORK_ERROR',
  'TIMEOUT_ERROR',
]);

function statusToCode(status: number): ErrorType {
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return 'VALIDATION_ERROR';
    case HTTP_STATUS.UNAUTHORIZED:
      return 'AUTHENTICATION_ERROR';
    case HTTP_STATUS.FORBIDDEN:
      return 'AUTHORIZATION_ERROR';
    case HTTP_STATUS.NOT_FOUND:
      return 'NOT_FOUND_ERROR';
    case HTTP_STATUS.UNPROCESSABLE_ENTITY:
      return 'VALIDATION_ERROR';
    case HTTP_STATUS.TOO_MANY_REQUESTS:
      return 'RATE_LIMIT_ERROR';
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'UNKNOWN_ERROR';
  }
}

const ERROR_TYPES: ReadonlySet<string> = new Set<ErrorType>([
  'NETWORK_ERROR',
  'TIMEOUT_ERROR',
  'VALIDATION_ERROR',
  'AUTHENTICATION_ERROR',
  'AUTHORIZATION_ERROR',
  'NOT_FOUND_ERROR',
  'RATE_LIMIT_ERROR',
  'SERVER_ERROR',
  'UNKNOWN_ERROR',
]);

/**
 * An AxiosError also carries `message`, `code` and `status`, so a loose
 * duck-type check matches it and normalizeError would hand it straight back
 * unnormalized -- leaving `code` as something like "ERR_BAD_REQUEST" that
 * getErrorMessage cannot map. Hence the explicit isAxiosError guard and the
 * check that `code` is one of our own ErrorType values.
 */
export function isApiError(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) return false;
  if ((value as { isAxiosError?: unknown }).isAxiosError === true) return false;

  const candidate = value as Partial<ApiError>;
  return (
    typeof candidate.message === 'string' &&
    typeof candidate.status === 'number' &&
    typeof candidate.code === 'string' &&
    ERROR_TYPES.has(candidate.code)
  );
}

/** Build a properly shaped ApiError. Used by the mock services so mocked and
 *  real failures travel the exact same path through getErrorMessage. */
export function createApiError(init: {
  message: string;
  code: ErrorType;
  status: number;
  details?: Record<string, string[]>;
}): ApiError {
  return { ...init };
}

interface AxiosLikeError {
  isAxiosError?: boolean;
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

function readServerPayload(data: unknown): {
  message?: string;
  details?: Record<string, string[]>;
} {
  if (typeof data !== 'object' || data === null) {
    return typeof data === 'string' && data.trim() ? { message: data } : {};
  }
  const body = data as { message?: unknown; error?: unknown; errors?: unknown };
  const message =
    typeof body.message === 'string' && body.message.trim()
      ? body.message
      : typeof body.error === 'string' && body.error.trim()
        ? body.error
        : undefined;
  const details: Record<string, string[]> = {};
  if (Array.isArray(body.errors)) {
    for (const entry of body.errors) {
      if (typeof entry !== 'object' || entry === null) continue;
      const validationError = entry as { field?: unknown; message?: unknown };
      if (
        typeof validationError.field === 'string'
        && typeof validationError.message === 'string'
        && validationError.message.trim()
      ) {
        (details[validationError.field] ??= []).push(validationError.message);
      }
    }
  } else if (typeof body.errors === 'object' && body.errors !== null) {
    for (const [field, messages] of Object.entries(body.errors as Record<string, unknown>)) {
      if (typeof messages === 'string' && messages.trim()) {
        details[field] = [messages];
      } else if (Array.isArray(messages)) {
        const validMessages = messages.filter(
          (item): item is string => typeof item === 'string' && item.trim().length > 0,
        );
        if (validMessages.length > 0) details[field] = validMessages;
      }
    }
  }
  return { message, ...(Object.keys(details).length > 0 ? { details } : {}) };
}

/** Turn anything thrown anywhere into a typed ApiError. Never throws. */
export function normalizeError(error: unknown): ApiError {
  if (isApiError(error)) return error;

  const axiosError = error as AxiosLikeError | null;

  if (axiosError && typeof axiosError === 'object' && axiosError.isAxiosError) {
    if (axiosError.response) {
      const status = axiosError.response.status ?? 0;
      const { message, details } = readServerPayload(axiosError.response.data);
      const code = statusToCode(status);
      return {
        message: message ?? axiosError.message ?? MESSAGES[code],
        code,
        status,
        ...(details ? { details } : {}),
      };
    }

    if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
      return { message: MESSAGES.TIMEOUT_ERROR, code: 'TIMEOUT_ERROR', status: 0 };
    }

    return { message: MESSAGES.NETWORK_ERROR, code: 'NETWORK_ERROR', status: 0 };
  }

  if (error instanceof Error) {
    return {
      message: error.message || MESSAGES.UNKNOWN_ERROR,
      code: 'UNKNOWN_ERROR',
      status: 0,
    };
  }

  return { message: MESSAGES.UNKNOWN_ERROR, code: 'UNKNOWN_ERROR', status: 0 };
}

/**
 * Statuses a partly-built backend returns for a route it does not serve yet:
 * 404 (no such route), 405 (route exists, verb does not), 501 (declared but
 * unimplemented).
 */
const ENDPOINT_MISSING_STATUSES: ReadonlySet<number> = new Set([
  HTTP_STATUS.NOT_FOUND,
  405,
  501,
]);

/**
 * Server copy for a missing route. Express answers "Cannot GET /x", ASP.NET
 * and several gateways answer "No endpoint ...". `getErrorMessage` passes a
 * server message through verbatim, so without this check that string lands on
 * screen, where it means nothing to a user.
 */
const ENDPOINT_MISSING_MESSAGE =
  /no endpoint|endpoint not found|not implemented|cannot (get|post|put|patch|delete)/i;

/**
 * "This route is not built yet", as distinct from "the request failed".
 *
 * The difference matters where a feature ships ahead of its API: a missing
 * endpoint should degrade to defaults quietly, while a real 500 or a
 * validation error still has to reach the user.
 */
export function isEndpointUnavailable(error: ApiError): boolean {
  if (error.code === 'NOT_FOUND_ERROR') return true;
  if (ENDPOINT_MISSING_STATUSES.has(error.status)) return true;
  return ENDPOINT_MISSING_MESSAGE.test(error.message);
}

/**
 * The per-field messages on a refused write, keyed as the server names the field (`name`, `scope.divisionIds`,
 * `sections[0].fields[2].label`, …).
 *
 * The server sends `errors: [{ field, message }]`; `normalizeError` carries that
 * body through as `details` without knowing its shape, so both it and the older
 * `{ field: [messages] }` map are read here.
 */
export function fieldErrorsOf(error: ApiError): Record<string, string> {
  const details: unknown = error.details;
  const out: Record<string, string> = {};

  if (Array.isArray(details)) {
    for (const entry of details) {
      if (typeof entry !== 'object' || entry === null) continue;
      const { field, message } = entry as { field?: unknown; message?: unknown };
      if (typeof field === 'string' && typeof message === 'string' && out[field] === undefined) {
        out[field] = message;
      }
    }
    return out;
  }

  if (typeof details === 'object' && details !== null) {
    for (const [field, messages] of Object.entries(details as Record<string, unknown>)) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (typeof first === 'string') out[field] = first;
    }
  }

  return out;
}

/**
 * Pick the copy the user sees.
 *
 * A real server message wins -- otherwise a 401 saying "Invalid email or
 * password" would be replaced by the generic "Session expired. Please login
 * again." The two transport-level codes are the exception, because there the
 * raw message comes from axios rather than from the API.
 */
export function getErrorMessage(error: ApiError): string {
  if (CODE_WINS.has(error.code)) return MESSAGES[error.code];
  return error.message.trim() || MESSAGES[error.code];
}
